import type { DocMigration } from "../docMigration";

interface WallFramingOpeningJson {
  hasCrippleStudEnds?: boolean;
  hasCrippleStudsBelowOpening?: boolean;
  hasCrippleStudsAboveOpening?: boolean;
  numberOfKingStuds?: number;
  headerAssemblyId?: string;
  numberOfJackStuds?: number;
  roughOpeningBorder?: number;
}

interface EntityWithOptionalFraming {
  framing?: WallFramingOpeningJson;
}

interface MigrationOpeningJson extends EntityWithOptionalFraming {
  readonly entityType?: string;
}

interface HeaderFramingRuleJson {
  readonly maxSpan?: number;
  readonly headerAssemblyId?: string;
  readonly jackStudCount?: number;
}

interface HeaderFramingSettingsJson {
  readonly type: "HEADER_FRAMING_SETTINGS";
  rules?: HeaderFramingRuleJson[];
}

interface DoorFramingSettingsJson extends WallFramingOpeningJson {
  readonly type: "DOOR_FRAMING_SETTINGS";
}

interface WindowFramingSettingsJson extends WallFramingOpeningJson {
  readonly type: "WINDOW_FRAMING_SETTINGS";
}

interface DoorDescriptorJson extends EntityWithOptionalFraming {
  readonly id?: string;
}

interface WindowDescriptorJson extends EntityWithOptionalFraming {
  readonly id?: string;
}

interface DoorCatalogSettingsJson {
  readonly type: "DOOR_CATALOG_SETTINGS";
  doorTypes?: Record<string, DoorDescriptorJson>;
}

interface WindowCatalogSettingsJson {
  readonly type: "WINDOW_CATALOG_SETTINGS";
  windowTypes?: Record<string, WindowDescriptorJson>;
}

type MigrationSettingsEntity =
  | HeaderFramingSettingsJson
  | DoorFramingSettingsJson
  | WindowFramingSettingsJson
  | DoorCatalogSettingsJson
  | WindowCatalogSettingsJson;

export interface MigrationDocJson {
  headerAssemblies?: Record<string, unknown>;
  openings?: Record<string, MigrationOpeningJson>;
  settings?: Record<string, unknown>;
}

export function asMigrationSettingsEntity(value: unknown): MigrationSettingsEntity | undefined {
  if (typeof value !== "object" || value === null || !("type" in value)) {
    return undefined;
  }
  const { type } = value as { type: unknown };
  if (
    type === "HEADER_FRAMING_SETTINGS" ||
    type === "DOOR_FRAMING_SETTINGS" ||
    type === "WINDOW_FRAMING_SETTINGS" ||
    type === "DOOR_CATALOG_SETTINGS" ||
    type === "WINDOW_CATALOG_SETTINGS"
  ) {
    return value as MigrationSettingsEntity;
  }
  return undefined;
}

function framingJsonIsEmpty(framing: WallFramingOpeningJson): boolean {
  for (const key in framing) {
    if (Object.prototype.hasOwnProperty.call(framing, key)) {
      return false;
    }
  }
  return true;
}

function clearDanglingFramingReference(entity: EntityWithOptionalFraming, validHeaderAssemblyIds: Set<string>): void {
  const framing = entity.framing;
  if (framing?.headerAssemblyId === undefined || validHeaderAssemblyIds.has(framing.headerAssemblyId)) {
    return;
  }
  const cleaned: WallFramingOpeningJson = { ...framing };
  delete cleaned.headerAssemblyId;
  if (framingJsonIsEmpty(cleaned)) {
    delete entity.framing;
  } else {
    entity.framing = cleaned;
  }
}

function clearDanglingFramingSettingsReference(
  settings: DoorFramingSettingsJson | WindowFramingSettingsJson,
  validHeaderAssemblyIds: Set<string>,
): void {
  if (settings.headerAssemblyId === undefined || validHeaderAssemblyIds.has(settings.headerAssemblyId)) {
    return;
  }
  delete settings.headerAssemblyId;
}

function clearDanglingHeaderFramingRules(
  settings: HeaderFramingSettingsJson,
  validHeaderAssemblyIds: Set<string>,
): void {
  const rules = settings.rules;
  if (rules === undefined) {
    return;
  }
  settings.rules = rules.filter(
    (rule) => rule.headerAssemblyId !== undefined && validHeaderAssemblyIds.has(rule.headerAssemblyId),
  );
}

function migrateDoc(doc: MigrationDocJson): void {
  const validHeaderAssemblyIds = new Set(Object.keys(doc.headerAssemblies ?? {}));

  const openings = doc.openings;
  if (openings !== undefined) {
    for (const openingId in openings) {
      const opening = openings[openingId];
      if (opening?.entityType === "OPENING") {
        clearDanglingFramingReference(opening, validHeaderAssemblyIds);
      }
    }
  }

  const settings = doc.settings;
  if (settings !== undefined) {
    for (const settingsId in settings) {
      const setting = asMigrationSettingsEntity(settings[settingsId]);
      if (setting === undefined) {
        continue;
      }
      if (setting.type === "HEADER_FRAMING_SETTINGS") {
        clearDanglingHeaderFramingRules(setting, validHeaderAssemblyIds);
      }
      if (setting.type === "DOOR_FRAMING_SETTINGS" || setting.type === "WINDOW_FRAMING_SETTINGS") {
        clearDanglingFramingSettingsReference(setting, validHeaderAssemblyIds);
      }
      if (setting.type === "DOOR_CATALOG_SETTINGS") {
        const doorTypes = setting.doorTypes;
        if (doorTypes !== undefined) {
          for (const doorTypeId in doorTypes) {
            clearDanglingFramingReference(doorTypes[doorTypeId], validHeaderAssemblyIds);
          }
        }
      }
      if (setting.type === "WINDOW_CATALOG_SETTINGS") {
        const windowTypes = setting.windowTypes;
        if (windowTypes !== undefined) {
          for (const windowTypeId in windowTypes) {
            clearDanglingFramingReference(windowTypes[windowTypeId], validHeaderAssemblyIds);
          }
        }
      }
    }
  }
}

const migration: DocMigration = {
  id: "20260609_clear_dangling_header_assembly_refs",
  description: "Clear dangling header assembly references",
  apply(json, _warnings, _errors) {
    migrateDoc(json as MigrationDocJson);
    return json;
  },
};

export default migration;
